import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

import { ApiError } from '@/lib/api/client';
import {
  createCustomDashboardTabGroup,
  isConflict,
  renameCustomDashboardTab,
  renameCustomDashboardTabGroup,
  saveCustomDashboardTabLayout,
  type CustomDashboardTabLayout,
} from '@/lib/api/custom-dashboard';
import { allTabsQueryKey } from '../allTabsQueryKey';

export interface UseTabLayoutActionsResult {
  /** いずれかの操作の保存中か（保存中は他の操作を受け付けない）。 */
  isBusy: boolean;
  saveLayout: (body: CustomDashboardTabLayout, successMessage: string) => Promise<boolean>;
  createGroup: (name: string) => Promise<boolean>;
  renameGroup: (groupId: string, name: string) => Promise<boolean>;
  renameTab: (tabId: string, name: string) => Promise<boolean>;
}

/** 再取得して画面を最新にすべき失敗か（409: ほかのユーザーの追加・削除やデータ更新中、404: 削除済み）。 */
function shouldReload(error: unknown): boolean {
  return isConflict(error) || (error instanceof ApiError && error.status === 404);
}

/**
 * タブ設定画面の操作（配置の保存・グループの作成と改名・タブの改名）。操作ごとにその場で保存し、
 * 設定一覧と閲覧画面の一覧を読み直す。戻り値は保存できたか。
 */
export function useTabLayoutActions(
  accountId: string,
  authHeaders: Record<string, string>,
): UseTabLayoutActionsResult {
  const queryClient = useQueryClient();
  const [isBusy, setIsBusy] = useState(false);

  const refresh = () =>
    Promise.all([
      queryClient.invalidateQueries({ queryKey: allTabsQueryKey(accountId) }),
      // 閉じた後の閲覧画面に古い並び・名前が残らないよう、表示用の一覧も読み直す
      queryClient.invalidateQueries({ queryKey: ['custom-dashboard-tabs', accountId] }),
    ]);

  const run = async (
    operation: () => Promise<unknown>,
    successMessage: string,
    fallbackMessage: string,
  ): Promise<boolean> => {
    setIsBusy(true);
    try {
      await operation();
      toast.success(successMessage);
      await refresh();
      return true;
    } catch (error) {
      if (shouldReload(error)) await refresh();
      toast.error(error instanceof Error ? error.message : fallbackMessage);
      return false;
    } finally {
      setIsBusy(false);
    }
  };

  return {
    isBusy,
    saveLayout: (body, successMessage) =>
      run(
        () => saveCustomDashboardTabLayout(accountId, body, authHeaders),
        successMessage,
        'タブの配置を保存できませんでした',
      ),
    createGroup: (name) =>
      run(
        () => createCustomDashboardTabGroup(accountId, { name }, authHeaders),
        'タブグループを追加しました',
        'タブグループを追加できませんでした',
      ),
    renameGroup: (groupId, name) =>
      run(
        () => renameCustomDashboardTabGroup(accountId, groupId, { name }, authHeaders),
        'タブグループ名を変更しました',
        'タブグループ名を変更できませんでした',
      ),
    renameTab: (tabId, name) =>
      run(
        () => renameCustomDashboardTab(accountId, tabId, { name }, authHeaders),
        'タブ名を変更しました',
        'タブ名の変更に失敗しました',
      ),
  };
}
